import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useInvestigation } from '../hooks/useInvestigations';
import ErrorState from '../components/ErrorState';
import LoadingOverlay from '../components/LoadingOverlay';
import EmptyState from '../components/EmptyState';
import LineageGraph from '../components/investigations/LineageGraph';
import SectionIcon from '../components/investigations/SectionIcon';
import IncidentChat from '../components/investigations/IncidentChat';
import {
    api,
    getSessionCookieToken,
    remediationStatus,
    type RemediationSummary,
    type Evidence
} from '../api/client';

// ── Security Audit Slice helpers ──────────────────────────────────────────────

type AuditRow = { api: string; window: string };

function toolToAuditRow(toolName: string): AuditRow {
    const t = toolName.toLowerCase();
    if (t.includes('datadog'))
        return { api: 'Datadog API Query', window: '10m slice' };
    if (t.includes('grafana') && (t.includes('log') || t.includes('loki')))
        return { api: 'Grafana Loki Pull', window: '10m slice' };
    if (t.includes('grafana') && t.includes('metric'))
        return { api: 'Grafana Metrics', window: '10m slice' };
    if (t.includes('grafana') && t.includes('trace'))
        return { api: 'Grafana Traces', window: '10m slice' };
    if (t.includes('grafana'))
        return { api: 'Grafana Query', window: '10m slice' };
    if (
        t.includes('kubernetes') ||
        t.includes('eks') ||
        t.includes('pod') ||
        t.includes('k8s')
    )
        return { api: 'Kubernetes Pod GET', window: 'Current ns' };
    if (t.includes('prometheus'))
        return { api: 'Prometheus Query', window: '10m slice' };
    if (t.includes('cloudwatch'))
        return { api: 'CloudWatch Query', window: '10m slice' };
    if (t.includes('newrelic') || t.includes('new_relic'))
        return { api: 'New Relic Query', window: '10m slice' };
    if (t.includes('pagerduty') || t.includes('pager_duty'))
        return { api: 'PagerDuty Query', window: 'Current' };
    if (t.includes('snowflake'))
        return { api: 'Snowflake Query', window: '10m slice' };
    // Fallback: convert snake_case to Title Case
    const label = toolName
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    return { api: label, window: '—' };
}

function buildAuditRows(evidence: Evidence[]): AuditRow[] {
    const seen = new Set<string>();
    const rows: AuditRow[] = [];
    for (const ev of evidence) {
        const row = toolToAuditRow(ev.tool);
        if (!seen.has(row.api)) {
            seen.add(row.api);
            rows.push(row);
        }
    }
    return rows;
}

const STATUS_BADGE: Record<string, string> = {
    complete: 'badge-success',
    running: 'badge-info',
    stale: 'badge-error',
    failed: 'badge-error'
};

// Shown while the investigation row exists but its RCA is still being generated
// (status="running"). The detail query polls every 3s and re-renders the full
// report the moment the RCA lands.
function GeneratingRca({ alertName }: { id: string; alertName: string }) {
    return (
        <div className="page">
            <div className="page-header" style={{ marginBottom: '8px' }}>
                <Link to="/investigations" className="back-link">
                    ← Back to incident console
                </Link>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        marginTop: '12px'
                    }}
                >
                    <div>
                        <h1 className="text-display-md" style={{ margin: 0 }}>
                            {alertName}
                        </h1>
                    </div>
                    <span
                        className={`badge ${STATUS_BADGE.running}`}
                        style={{ padding: '6px 14px', fontSize: '12px' }}
                    >
                        running
                    </span>
                </div>
            </div>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '14px',
                    padding: '48px 24px',
                    textAlign: 'center',
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px'
                }}
            >
                <span
                    className="evidence-dot-check spin"
                    style={{ width: '18px', height: '18px' }}
                    aria-hidden
                />
                <div>
                    <p style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>
                        Generating root cause analysis…
                    </p>
                    <p
                        style={{
                            margin: '6px 0 0',
                            color: 'var(--text-muted)',
                            fontSize: '13px'
                        }}
                    >
                        The agent is gathering evidence and validating
                        hypotheses. This usually takes under a minute — this
                        page updates automatically.
                    </p>
                </div>
                {/* Skeleton placeholders for the report that's about to land. */}
                <div
                    style={{
                        width: '100%',
                        maxWidth: '520px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        marginTop: '8px'
                    }}
                >
                    <div
                        className="skeleton-line"
                        style={{ height: '12px', width: '90%' }}
                    />
                    <div
                        className="skeleton-line"
                        style={{ height: '12px', width: '75%' }}
                    />
                    <div
                        className="skeleton-line"
                        style={{ height: '12px', width: '82%' }}
                    />
                </div>
            </div>
        </div>
    );
}

// ── Remediation state label + color map ───────────────────────────────────────
const REMEDIATION_LABEL: Record<string, string> = {
    idle: 'No fix queued',
    planning: 'Planning fix…',
    blocked: 'Action required',
    executing: 'Generating fix…',
    ready: 'Fix ready',
    failed: 'Fix failed',
    rca_mismatch: 'No code change needed'
};

const REMEDIATION_BADGE: Record<string, string> = {
    idle: 'badge-info',
    planning: 'badge-info',
    blocked: 'badge-warning',
    executing: 'badge-info',
    ready: 'badge-success',
    failed: 'badge-error',
    rca_mismatch: 'badge-info'
};

// ── GitHub connect CTA ────────────────────────────────────────────────────────
const GITHUB_ACCESS_ERRORS = new Set([
    'github_app_not_installed',
    'repo_access_required'
]);

function GitHubConnectBanner({
    remediation,
    onConnectGitHub
}: {
    remediation: RemediationSummary;
    onConnectGitHub: () => void;
}) {
    const status = remediationStatus(remediation);
    const errorCode = remediation.meta.error_code;
    if (
        status !== 'blocked' ||
        !errorCode ||
        !GITHUB_ACCESS_ERRORS.has(errorCode)
    ) {
        return null;
    }

    const message =
        remediation.meta.last_error ??
        (errorCode === 'repo_access_required'
            ? 'Grant repository access on GitHub to enable automated pull requests.'
            : 'Connect the SelfHeal GitHub App to enable automated pull requests.');

    return (
        <div
            style={{
                marginTop: '16px',
                padding: '12px 14px',
                borderRadius: '8px',
                border: '1px solid rgba(var(--warning-rgb, 232, 165, 90), 0.35)',
                background: 'rgba(var(--warning-rgb, 232, 165, 90), 0.08)'
            }}
        >
            <p
                style={{
                    margin: 0,
                    fontSize: '13px',
                    color: 'var(--text-h)',
                    lineHeight: 1.5
                }}
            >
                {message}
            </p>
            <button
                type="button"
                className="btn-coral-cta"
                style={{ marginTop: '10px', width: '100%' }}
                onClick={onConnectGitHub}
            >
                {errorCode === 'repo_access_required'
                    ? 'Connect repository on GitHub'
                    : 'Connect GitHub App'}
            </button>
        </div>
    );
}

function RemediationPanel({
    remediation,
    investigationId,
    onRetry,
    retrying,
    retryError,
    onConnectGitHub,
    onReconnectGitHub,
    reconnecting
}: {
    remediation: RemediationSummary | null;
    investigationId: string;
    onRetry: () => void;
    retrying: boolean;
    retryError: string | null;
    onConnectGitHub: () => void;
    onReconnectGitHub: () => void;
    reconnecting: boolean;
}) {
    const status = remediationStatus(remediation);
    const prArtifact = remediation?.artifacts.find((a) => a.url);
    const errorCode = remediation?.meta.error_code;
    const lastError = remediation?.meta.last_error;
    const branch = remediation?.plan?.[0]
        ? `healops/fix-${investigationId.slice(0, 8)}`
        : null;

    const isSpinning = status === 'planning' || status === 'executing';
    const hasCodeFix =
        remediation?.plan?.some((entry) => entry?.fix_type === 'code') ?? false;
    const showReconnectGithub =
        hasCodeFix &&
        (status === 'planning' ||
            status === 'failed' ||
            status === 'executing' ||
            (status === 'blocked' &&
                typeof errorCode === 'string' &&
                GITHUB_ACCESS_ERRORS.has(errorCode)));
    const showRetry =
        status === 'failed' ||
        status === 'planning' ||
        (status === 'blocked' &&
            typeof errorCode === 'string' &&
            GITHUB_ACCESS_ERRORS.has(errorCode));

    return (
        <div className="merge-gate-box">
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px'
                }}
            >
                <span className="merge-gate-badge" style={{ margin: 0 }}>
                    Auto-Remediation
                </span>
                <span
                    className={`badge ${REMEDIATION_BADGE[status] ?? 'badge-info'}`}
                    style={{ fontSize: '11px', padding: '3px 10px' }}
                >
                    {isSpinning && (
                        <span
                            className="evidence-dot-check spin"
                            style={{
                                width: '8px',
                                height: '8px',
                                marginRight: '6px',
                                display: 'inline-block'
                            }}
                        />
                    )}
                    {REMEDIATION_LABEL[status] ?? status}
                </span>
            </div>

            <div className="merge-gate-header">
                <h3 className="merge-gate-title">Remediation</h3>
            </div>

            <div className="merge-gate-meta">
                {branch && (
                    <div className="merge-meta-item">
                        <span>Branch:</span>
                        <code>{branch}</code>
                    </div>
                )}

                {status === 'ready' && prArtifact && (
                    <div
                        className="merge-meta-item"
                        style={{ flexDirection: 'column', gap: '4px' }}
                    >
                        {prArtifact.repo && (
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <span>Repo:</span>
                                <code style={{ fontSize: '11px' }}>
                                    {prArtifact.repo}
                                </code>
                            </div>
                        )}
                        {typeof prArtifact.files_changed === 'number' && (
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <span>Files changed:</span>
                                <strong>{prArtifact.files_changed}</strong>
                            </div>
                        )}
                    </div>
                )}

                {(status === 'blocked' || status === 'failed') && lastError && (
                    <div
                        style={{
                            marginTop: '8px',
                            padding: '8px 10px',
                            background:
                                'rgba(var(--error-rgb, 235,68,68),0.08)',
                            borderRadius: '6px',
                            border: '1px solid rgba(var(--error-rgb,235,68,68),0.2)'
                        }}
                    >
                        <span
                            style={{
                                fontSize: '11.5px',
                                color: 'var(--error)',
                                lineHeight: '1.5'
                            }}
                        >
                            {lastError}
                        </span>
                    </div>
                )}

                {status === 'rca_mismatch' && (
                    <div
                        style={{
                            marginTop: '8px',
                            padding: '10px 12px',
                            background: 'var(--bg-soft)',
                            borderRadius: '6px',
                            border: '1px solid var(--border)'
                        }}
                    >
                        <div
                            style={{
                                fontSize: '12px',
                                fontWeight: 600,
                                marginBottom: '4px'
                            }}
                        >
                            No code change applied — root cause not confirmed in
                            the code
                        </div>
                        <span
                            style={{
                                fontSize: '11.5px',
                                color: 'var(--text-soft)',
                                lineHeight: '1.55'
                            }}
                        >
                            The agent reviewed the repository and did not find
                            the reported root cause in the code, so it did not
                            open a fix. Exact reason:
                        </span>
                        <div
                            style={{
                                marginTop: '6px',
                                fontSize: '11.5px',
                                color: 'var(--text)',
                                lineHeight: '1.55',
                                whiteSpace: 'pre-wrap'
                            }}
                        >
                            {lastError ||
                                'The code contradicts the stated root cause.'}
                        </div>
                        <div
                            style={{
                                marginTop: '6px',
                                fontSize: '11px',
                                color: 'var(--text-soft)'
                            }}
                        >
                            The root cause above has been updated to the agent's
                            code-verified finding.
                        </div>
                    </div>
                )}

                {(status === 'planning' || status === 'executing') && (
                    <div
                        style={{
                            marginTop: '8px',
                            padding: '8px 10px',
                            background: 'var(--bg-soft)',
                            borderRadius: '6px',
                            border: '1px solid var(--border)'
                        }}
                    >
                        <span
                            style={{
                                fontSize: '11.5px',
                                color: 'var(--text-muted)'
                            }}
                        >
                            {status === 'planning'
                                ? 'Building remediation plan…'
                                : 'Sandbox agent is generating a code fix. This usually takes under a minute.'}
                        </span>
                    </div>
                )}

                {retryError && (
                    <div
                        style={{
                            marginTop: '8px',
                            padding: '8px 10px',
                            background:
                                'rgba(var(--error-rgb, 235,68,68),0.08)',
                            borderRadius: '6px',
                            border: '1px solid rgba(var(--error-rgb,235,68,68),0.2)'
                        }}
                    >
                        <span
                            style={{
                                fontSize: '11.5px',
                                color: 'var(--error)',
                                lineHeight: '1.5'
                            }}
                        >
                            {retryError}
                        </span>
                    </div>
                )}
            </div>

            <div
                className="merge-actions"
                style={{ flexDirection: 'column', gap: '8px' }}
            >
                {/* Primary CTA: View pull request (AC-3). Hidden for rca_mismatch — there is
            no PR and never will be (the agent declined to change code). */}
                {status === 'ready' && prArtifact?.url ? (
                    <a
                        href={prArtifact.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-coral-cta"
                        style={{
                            width: '100%',
                            textAlign: 'center',
                            textDecoration: 'none',
                            display: 'block'
                        }}
                    >
                        View pull request ↗
                    </a>
                ) : status === 'rca_mismatch' ? null : (
                    <button
                        className="btn-coral-cta"
                        style={{ width: '100%' }}
                        disabled
                    >
                        {status === 'executing' || status === 'planning'
                            ? 'Generating fix…'
                            : 'View pull request'}
                    </button>
                )}

                {/* Connect GitHub CTA (AC-4) */}
                {status === 'blocked' &&
                    errorCode &&
                    GITHUB_ACCESS_ERRORS.has(errorCode) && (
                        <button
                            className="btn-secondary-outline"
                            style={{ width: '100%' }}
                            onClick={onConnectGitHub}
                        >
                            {errorCode === 'repo_access_required'
                                ? 'Connect repository on GitHub'
                                : 'Connect GitHub App'}
                        </button>
                    )}

                {showReconnectGithub &&
                    !(
                        status === 'blocked' &&
                        errorCode &&
                        GITHUB_ACCESS_ERRORS.has(errorCode)
                    ) && (
                        <button
                            className="btn-secondary-outline"
                            style={{ width: '100%' }}
                            disabled={reconnecting}
                            onClick={onReconnectGitHub}
                        >
                            {reconnecting
                                ? 'Opening GitHub…'
                                : 'Reconnect GitHub App'}
                        </button>
                    )}

                {/* Retry after failure or when planning is stuck (AC-5) */}
                {showRetry && (
                    <button
                        className="btn-secondary-outline"
                        style={{ width: '100%' }}
                        disabled={retrying}
                        onClick={onRetry}
                    >
                        {retrying ? 'Retrying…' : 'Retry fix'}
                    </button>
                )}
            </div>
        </div>
    );
}

export default function InvestigationDetail() {
    const { id } = useParams<{ id: string }>();
    const { getToken } = useAuth();
    const queryClient = useQueryClient();
    const { data, isLoading, isError, error, refetch } = useInvestigation(
        id ?? ''
    );

    // Interactive state
    const [activeIdeTab, setActiveIdeTab] = useState<number>(0);
    const [copied, setCopied] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [retryError, setRetryError] = useState<string | null>(null);
    const [connecting, setConnecting] = useState(false);
    const [reconnecting, setReconnecting] = useState(false);

    async function handleRetryRemediation() {
        if (!id || retrying) return;
        setRetrying(true);
        setRetryError(null);
        try {
            const token = getSessionCookieToken() ?? (await getToken());
            if (!token) throw new Error('Not authenticated');
            await api.retryRemediation(token, id);
            await refetch();
            // Refresh the list so its status + triage progress reflect the in-flight retry.
            void queryClient.invalidateQueries({
                queryKey: ['investigations']
            });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Retry failed';
            setRetryError(message);
            console.error('Retry remediation failed', err);
        } finally {
            setRetrying(false);
        }
    }

    async function handleConnectGitHub() {
        if (!id || connecting) return;
        setConnecting(true);
        try {
            const token = getSessionCookieToken() ?? (await getToken());
            if (!token) throw new Error('Not authenticated');
            const { install_url } = await api.getGithubInstallUrl(
                token,
                window.location.pathname,
                id
            );
            window.open(install_url, '_blank', 'noopener,noreferrer');
        } catch (err) {
            console.error('Failed to get GitHub install URL', err);
        } finally {
            setConnecting(false);
        }
    }

    async function handleReconnectGitHub() {
        if (!id || reconnecting) return;
        setReconnecting(true);
        try {
            const token = getSessionCookieToken() ?? (await getToken());
            if (!token) throw new Error('Not authenticated');
            await api.disconnectGithub(token);
            const { install_url } = await api.getGithubInstallUrl(
                token,
                window.location.pathname,
                id
            );
            window.open(install_url, '_blank', 'noopener,noreferrer');
            await refetch();
        } catch (err) {
            console.error('Failed to reconnect GitHub App', err);
        } finally {
            setReconnecting(false);
        }
    }

    if (isLoading) return <LoadingOverlay minHeight="240px" />;
    if (isError) {
        const msg = (error as Error).message;
        if (msg.includes('404'))
            return (
                <EmptyState
                    message="Investigation not found."
                    hint="It may have been deleted or the ID is incorrect."
                />
            );
        return (
            <ErrorState
                message={`Failed to load: ${msg}`}
                onRetry={() => void refetch()}
            />
        );
    }
    if (!data) return null;

    // RCA is still being generated — show a polling skeleton instead of an empty report.
    // status is authoritative (a running row never carries an RCA yet).
    if (data.status === 'running') {
        return <GeneratingRca id={data.id} alertName={data.alert_name} />;
    }

    const rca = data.rca;
    const impactGraph = rca?.graph?.impact ?? null;
    const rootCauseGraph = rca?.graph?.root_cause ?? null;
    const recommendations = rca?.recommendations ?? [];
    const alertName = data.alert_name;
    const isDbAlert = /postgres|db|database|pool|sql|connection/i.test(
        alertName
    );
    const hasCodeFix =
        data.remediation?.plan?.some((entry) => entry?.fix_type === 'code') ??
        false;
    const remediationState = remediationStatus(data.remediation);
    // The agent reviewed the code and found the reported root cause isn't there, so
    // no code fix applies. Explain this instead of a misleading "fix coming" panel.
    const isRcaMismatch = remediationState === 'rca_mismatch';
    // The agent's specific, code-grounded explanation (falls back to the raw reason).
    const rcaMismatchReason =
        data.remediation?.meta?.rca_mismatch_explanation ??
        data.remediation?.meta?.last_error ??
        '';

    // Real investigation journey — one step per tool-execution loop, ordered.
    const journeySteps = [...(rca?.evidence ?? [])].sort(
        (a, b) => (a.step_order ?? 0) - (b.step_order ?? 0)
    );
    const findings = rca?.findings ?? [];
    const confirmedFindings = findings.filter((f) => f.validated);
    const inferredFindings = findings.filter((f) => !f.validated);
    const hasRemediation =
        (data.remediation?.plan?.length ?? 0) > 0 ||
        (data.remediation?.artifacts?.length ?? 0) > 0;

    // Parse unified diff string into per-file sections for tab display
    function parseDiffFiles(
        diff: string
    ): Array<{ path: string; content: string }> {
        if (!diff) return [];
        const sections = diff.split(/^diff --git /m).filter(Boolean);
        return sections.map((section) => {
            const firstLine = section.split('\n')[0] ?? '';
            const match = firstLine.match(/^a\/.+ b\/(.+)$/);
            const path = match
                ? match[1]
                : (firstLine.split(' ').pop() ?? 'unknown');
            return { path, content: 'diff --git ' + section };
        });
    }

    const codeArtifact = data.remediation?.artifacts?.find(
        (a) => a.operation === 'github_draft_pr'
    );
    const unifiedDiff = codeArtifact?.extra?.unified_diff ?? '';
    const diffFiles = parseDiffFiles(unifiedDiff);

    // Copy code diff helper
    function handleCopyDiff() {
        const content = diffFiles[activeIdeTab]?.content ?? unifiedDiff;
        navigator.clipboard.writeText(content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }

    return (
        <div className="page">
            {/* ── Page Header ────────────────────────────────────────── */}
            <div className="page-header" style={{ marginBottom: '8px' }}>
                <Link to="/investigations" className="back-link">
                    ← Back to incident console
                </Link>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        marginTop: '12px'
                    }}
                >
                    <div>
                        <h1 className="text-display-md" style={{ margin: 0 }}>
                            {data.alert_name}
                        </h1>
                    </div>
                    <span
                        className={`badge ${STATUS_BADGE[data.status] ?? 'badge-info'}`}
                        style={{ padding: '6px 14px', fontSize: '12px' }}
                    >
                        {data.status}
                    </span>
                </div>
            </div>

            {/* ── Two-Column Command Center Layout ────────────────────── */}
            <div className="two-column-layout">
                {/* Left Main Panel */}
                <div className="main-panel">
                    {/* Impact — blast radius of the incident (secondary; scrolls below the fold) */}
                    {impactGraph && impactGraph.nodes.length > 0 && (
                        <div
                            className="summary-card"
                            style={{ padding: '24px', order: 5 }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    marginBottom: '8px'
                                }}
                            >
                                <SectionIcon name="impact" />
                                <h2
                                    style={{
                                        fontSize: '15px',
                                        fontWeight: 600,
                                        color: 'var(--text-h)',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        margin: 0
                                    }}
                                >
                                    Impact
                                </h2>
                            </div>
                            <p
                                style={{
                                    fontSize: '14px',
                                    color: 'var(--text-h)',
                                    lineHeight: '1.6',
                                    margin: '4px 0 16px'
                                }}
                            >
                                {impactGraph.summary}
                            </p>
                            <LineageGraph
                                graph={impactGraph}
                                height={300}
                                ariaLabel="Downstream impact graph"
                            />
                            <p
                                style={{
                                    fontSize: '11px',
                                    color: 'var(--text-muted)',
                                    textAlign: 'right',
                                    margin: '6px 2px 0'
                                }}
                            >
                                scroll to zoom · drag to pan
                            </p>
                        </div>
                    )}

                    {/* Investigation Journey — the real steps the agent took to reach the root cause */}
                    <div
                        className="summary-card"
                        style={{ padding: '24px', order: 6 }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                marginBottom: '8px'
                            }}
                        >
                            <SectionIcon name="journey" />
                            <h2
                                style={{
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    color: 'var(--text-h)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    margin: 0
                                }}
                            >
                                Investigation Journey
                            </h2>
                        </div>
                        <p
                            style={{
                                fontSize: '13px',
                                color: 'var(--text-muted)',
                                margin: '0 0 16px'
                            }}
                        >
                            How SelfHeal reasoned from the alert to the root
                            cause. Each step is a real tool the agent chose to
                            run and what it found.
                        </p>

                        {journeySteps.length > 0 ? (
                            <ol
                                style={{
                                    listStyle: 'none',
                                    padding: 0,
                                    margin: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '14px'
                                }}
                            >
                                {journeySteps.map((step, i) => {
                                    const hypothesis =
                                        step.query &&
                                        !/^Agent iteration/i.test(step.query)
                                            ? step.query
                                            : '';
                                    const stepFindings = step.findings ?? [];
                                    return (
                                        <li
                                            key={i}
                                            style={{
                                                borderLeft:
                                                    '2px solid var(--border)',
                                                paddingLeft: '16px',
                                                position: 'relative'
                                            }}
                                        >
                                            <span
                                                style={{
                                                    position: 'absolute',
                                                    left: '-9px',
                                                    top: '2px',
                                                    width: '16px',
                                                    height: '16px',
                                                    borderRadius: '50%',
                                                    background: 'var(--accent)',
                                                    color: '#fff',
                                                    fontSize: '9px',
                                                    fontWeight: 700,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                {i + 1}
                                            </span>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    flexWrap: 'wrap'
                                                }}
                                            >
                                                <strong
                                                    style={{
                                                        fontSize: '13.5px',
                                                        color: 'var(--text-h)'
                                                    }}
                                                >
                                                    Queried {step.tool}
                                                </strong>
                                                {(step.sources ?? []).map(
                                                    (s) => (
                                                        <span
                                                            key={s}
                                                            className="audit-scope-tag"
                                                            style={{
                                                                fontSize: '9px'
                                                            }}
                                                        >
                                                            {s}
                                                        </span>
                                                    )
                                                )}
                                            </div>
                                            {hypothesis && (
                                                <p
                                                    style={{
                                                        fontSize: '12.5px',
                                                        color: 'var(--text-muted)',
                                                        margin: '4px 0 0'
                                                    }}
                                                >
                                                    <em>Hypothesis:</em>{' '}
                                                    {hypothesis}
                                                </p>
                                            )}
                                            {stepFindings.length > 0 ? (
                                                <ul
                                                    style={{
                                                        margin: '8px 0 0',
                                                        paddingLeft: '16px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '4px'
                                                    }}
                                                >
                                                    {stepFindings
                                                        .slice(0, 4)
                                                        .map((f, fi) => (
                                                            <li
                                                                key={fi}
                                                                style={{
                                                                    fontSize:
                                                                        '11.5px',
                                                                    color: 'var(--text)',
                                                                    fontFamily:
                                                                        'var(--mono)',
                                                                    wordBreak:
                                                                        'break-word'
                                                                }}
                                                            >
                                                                {f}
                                                            </li>
                                                        ))}
                                                </ul>
                                            ) : (
                                                <p
                                                    style={{
                                                        fontSize: '11.5px',
                                                        color: 'var(--text-muted)',
                                                        margin: '6px 0 0'
                                                    }}
                                                >
                                                    No structured findings
                                                    captured for this step.
                                                </p>
                                            )}
                                        </li>
                                    );
                                })}
                            </ol>
                        ) : (
                            <p
                                style={{
                                    fontSize: '13px',
                                    color: 'var(--text-muted)',
                                    margin: 0
                                }}
                            >
                                No tool-execution steps were recorded for this
                                investigation.
                            </p>
                        )}

                        {/* Agent conclusions — evidence-backed (confirmed) vs inferred */}
                        {findings.length > 0 && (
                            <div
                                style={{
                                    marginTop: '20px',
                                    borderTop: '1px solid var(--border)',
                                    paddingTop: '16px'
                                }}
                            >
                                <h4
                                    style={{
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: 'var(--accent-light)',
                                        textTransform: 'uppercase',
                                        marginBottom: '10px'
                                    }}
                                >
                                    Agent Conclusions
                                </h4>
                                <ul
                                    style={{
                                        listStyle: 'none',
                                        padding: 0,
                                        margin: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px'
                                    }}
                                >
                                    {confirmedFindings.map((f, i) => (
                                        <li
                                            key={`c${i}`}
                                            style={{
                                                display: 'flex',
                                                gap: '8px',
                                                alignItems: 'flex-start'
                                            }}
                                        >
                                            <span
                                                style={{
                                                    color: 'var(--success)',
                                                    fontWeight: 700,
                                                    fontSize: '13px',
                                                    lineHeight: '1.5'
                                                }}
                                            >
                                                ✓
                                            </span>
                                            <span
                                                style={{
                                                    fontSize: '13px',
                                                    color: 'var(--text)',
                                                    lineHeight: '1.5'
                                                }}
                                            >
                                                {f.claim}
                                                {f.evidence_labels.length >
                                                    0 && (
                                                    <span
                                                        style={{
                                                            color: 'var(--text-muted)',
                                                            fontFamily:
                                                                'var(--mono)',
                                                            fontSize: '11px'
                                                        }}
                                                    >
                                                        {' '}
                                                        [
                                                        {f.evidence_labels.join(
                                                            ', '
                                                        )}
                                                        ]
                                                    </span>
                                                )}
                                            </span>
                                        </li>
                                    ))}
                                    {inferredFindings.map((f, i) => (
                                        <li
                                            key={`n${i}`}
                                            style={{
                                                display: 'flex',
                                                gap: '8px',
                                                alignItems: 'flex-start'
                                            }}
                                        >
                                            <span
                                                style={{
                                                    color: 'var(--warning)',
                                                    fontWeight: 700,
                                                    fontSize: '13px',
                                                    lineHeight: '1.5'
                                                }}
                                            >
                                                ~
                                            </span>
                                            <span
                                                style={{
                                                    fontSize: '13px',
                                                    color: 'var(--text-muted)',
                                                    lineHeight: '1.5'
                                                }}
                                            >
                                                {f.claim}{' '}
                                                <span
                                                    style={{
                                                        fontSize: '10px',
                                                        textTransform:
                                                            'uppercase'
                                                    }}
                                                >
                                                    (inferred)
                                                </span>
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* RCA Executive highlights — leads the first fold */}
                    <div
                        className="summary-card"
                        style={{ padding: '24px', order: 1 }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                marginBottom: '8px'
                            }}
                        >
                            <SectionIcon name="search" />
                            <h2
                                style={{
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    color: 'var(--text-h)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    margin: 0
                                }}
                            >
                                Root Cause
                            </h2>
                            {rca?.corrected && (
                                <span
                                    className="badge badge-info"
                                    style={{
                                        fontSize: '10.5px',
                                        padding: '2px 8px'
                                    }}
                                    title="The original root cause was not confirmed in the code. It was corrected to the agent's code-verified finding."
                                >
                                    Corrected after code review
                                </span>
                            )}
                        </div>
                        <p
                            style={{
                                fontSize: '15px',
                                color: 'var(--text-h)',
                                lineHeight: '1.6',
                                margin: '4px 0 0'
                            }}
                        >
                            {rca?.root_cause ?? data.summary}
                        </p>
                        {rca?.corrected && rca.original_root_cause && (
                            <p
                                style={{
                                    fontSize: '12px',
                                    color: 'var(--text-soft)',
                                    lineHeight: '1.55',
                                    margin: '8px 0 0',
                                    textDecoration: 'line-through',
                                    opacity: 0.8
                                }}
                                title="Original root cause, superseded after code verification"
                            >
                                Was: {rca.original_root_cause}
                            </p>
                        )}
                        {rca && rca.contributing_factors.length > 0 && (
                            <div
                                style={{
                                    marginTop: '16px',
                                    borderTop: '1px solid var(--border)',
                                    paddingTop: '16px'
                                }}
                            >
                                <h4
                                    style={{
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: 'var(--accent-light)',
                                        textTransform: 'uppercase',
                                        marginBottom: '8px'
                                    }}
                                >
                                    Contributing Factors:
                                </h4>
                                <ul
                                    style={{
                                        paddingLeft: '20px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '6px',
                                        color: 'var(--text)'
                                    }}
                                >
                                    {rca.contributing_factors.map((f, i) => (
                                        <li key={i}>{f}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {rootCauseGraph && rootCauseGraph.nodes.length > 0 && (
                            <div
                                style={{
                                    marginTop: '16px',
                                    borderTop: '1px solid var(--border)',
                                    paddingTop: '16px'
                                }}
                            >
                                <h4
                                    style={{
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: 'var(--accent-light)',
                                        textTransform: 'uppercase',
                                        marginBottom: '4px'
                                    }}
                                >
                                    Causal Chain
                                </h4>
                                <p
                                    style={{
                                        fontSize: '12.5px',
                                        color: 'var(--text-muted)',
                                        margin: '0 0 12px'
                                    }}
                                >
                                    {rootCauseGraph.summary}
                                </p>
                                <LineageGraph
                                    graph={rootCauseGraph}
                                    height={260}
                                    ariaLabel="Upstream root-cause graph"
                                />
                                <p
                                    style={{
                                        fontSize: '11px',
                                        color: 'var(--text-muted)',
                                        textAlign: 'right',
                                        margin: '6px 2px 0'
                                    }}
                                >
                                    scroll to zoom · drag to pan
                                </p>
                            </div>
                        )}
                        {data.remediation && (
                            <GitHubConnectBanner
                                remediation={data.remediation}
                                onConnectGitHub={() =>
                                    void handleConnectGitHub()
                                }
                            />
                        )}
                    </div>

                    {/* Proposed Fix — recommended remediation steps (sits right under RCA) */}
                    {recommendations.length > 0 && (
                        <div
                            className="summary-card"
                            style={{ padding: '24px', order: 2 }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    marginBottom: '8px'
                                }}
                            >
                                <SectionIcon name="fix" />
                                <h2
                                    style={{
                                        fontSize: '15px',
                                        fontWeight: 600,
                                        color: 'var(--text-h)',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        margin: 0
                                    }}
                                >
                                    Proposed Fix
                                </h2>
                            </div>
                            <p
                                style={{
                                    fontSize: '13px',
                                    color: 'var(--text-muted)',
                                    margin: '0 0 12px'
                                }}
                            >
                                Recommended remediation and prevention steps.{' '}
                                {hasCodeFix
                                    ? 'A code patch is proposed below.'
                                    : ''}
                            </p>
                            <ol
                                style={{
                                    paddingLeft: '20px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px',
                                    color: 'var(--text)',
                                    fontSize: '14px',
                                    lineHeight: '1.6',
                                    margin: 0
                                }}
                            >
                                {recommendations.map((r, i) => (
                                    <li key={i}>{r}</li>
                                ))}
                            </ol>
                        </div>
                    )}

                    {/* rca_mismatch: no code patch is coming — explain why in plain language. */}
                    {isRcaMismatch && (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                                order: 3
                            }}
                        >
                            <h2
                                style={{
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: 'var(--text-h)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    margin: 0
                                }}
                            >
                                Why no code fix
                            </h2>
                            <div
                                style={{
                                    background: 'var(--bg-soft)',
                                    border: '1px solid var(--border)',
                                    borderRadius: '8px',
                                    padding: '16px 18px',
                                    fontSize: '13px',
                                    lineHeight: 1.6,
                                    color: 'var(--text)'
                                }}
                            >
                                <p style={{ margin: '0 0 10px' }}>
                                    We reviewed the actual code for the reported
                                    root cause and it isn't there — so there's
                                    nothing to fix in code. No pull request was
                                    opened, and the root cause above was
                                    corrected to what the review actually found.
                                </p>
                                {/* The agent's specific, code-grounded explanation of why no fix applies
                    and where the real cause lies (generated after reading the code). */}
                                {rcaMismatchReason && (
                                    <div
                                        style={{
                                            margin: 0,
                                            padding: '10px 12px',
                                            background: 'var(--bg)',
                                            border: '1px solid var(--border)',
                                            borderRadius: '6px',
                                            fontSize: '12.5px',
                                            color: 'var(--text)',
                                            lineHeight: 1.55,
                                            whiteSpace: 'pre-wrap'
                                        }}
                                    >
                                        {rcaMismatchReason}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* IDE Diff code editor — only shown when remediation plan has a code fix */}
                    {hasCodeFix && !isRcaMismatch && (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                                order: 4
                            }}
                        >
                            <h2
                                style={{
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: 'var(--text-h)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    margin: 0
                                }}
                            >
                                Proposed Fix — Code Patch
                            </h2>
                            {diffFiles.length > 0 ? (
                                <div className="ide-editor-container">
                                    {/* Tab headers — one per changed file */}
                                    <div className="ide-tabs">
                                        {diffFiles.map((file, idx) => {
                                            const isTest = /\btest[s]?\b/i.test(
                                                file.path
                                            );
                                            return (
                                                <button
                                                    key={idx}
                                                    className={`ide-tab ${activeIdeTab === idx ? 'active' : ''}`}
                                                    onClick={() =>
                                                        setActiveIdeTab(idx)
                                                    }
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '6px'
                                                    }}
                                                >
                                                    <SectionIcon
                                                        name={
                                                            isTest
                                                                ? 'test'
                                                                : 'file'
                                                        }
                                                        size={14}
                                                        color="currentColor"
                                                    />{' '}
                                                    {file.path}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Editor toolbar */}
                                    <div className="ide-toolbar">
                                        <span
                                            style={{
                                                color: 'var(--text-muted)',
                                                fontFamily: 'var(--mono)',
                                                fontSize: '11px'
                                            }}
                                        >
                                            Target branch:{' '}
                                            <code>
                                                {codeArtifact?.extra?.branch ??
                                                    `healops/fix-${data.id.slice(0, 8)}`}
                                            </code>{' '}
                                            → <code>main</code>
                                        </span>
                                        <button
                                            className="ide-btn"
                                            onClick={handleCopyDiff}
                                        >
                                            {copied ? 'Copied ✓' : 'Copy Diff'}
                                        </button>
                                    </div>

                                    {/* Code wrapper */}
                                    <div className="diff-code-wrapper">
                                        <pre style={{ margin: 0 }}>
                                            <code
                                                className="diff-table"
                                                style={{ display: 'block' }}
                                            >
                                                {(
                                                    diffFiles[activeIdeTab]
                                                        ?.content ?? ''
                                                )
                                                    .split('\n')
                                                    .map((line, i) => {
                                                        const isAdd =
                                                            line.startsWith(
                                                                '+'
                                                            ) &&
                                                            !line.startsWith(
                                                                '+++'
                                                            );
                                                        const isDel =
                                                            line.startsWith(
                                                                '-'
                                                            ) &&
                                                            !line.startsWith(
                                                                '---'
                                                            );
                                                        const isHeader =
                                                            line.startsWith(
                                                                '@@'
                                                            ) ||
                                                            line.startsWith(
                                                                '---'
                                                            ) ||
                                                            line.startsWith(
                                                                '+++'
                                                            ) ||
                                                            line.startsWith(
                                                                'diff '
                                                            );
                                                        let rowClass = '';
                                                        if (isAdd)
                                                            rowClass =
                                                                'addition';
                                                        else if (isDel)
                                                            rowClass =
                                                                'deletion';
                                                        else if (isHeader)
                                                            rowClass =
                                                                'info-row';

                                                        return (
                                                            <span
                                                                key={i}
                                                                className={`diff-row ${rowClass}`}
                                                                style={{
                                                                    display:
                                                                        'flex'
                                                                }}
                                                            >
                                                                <span className="diff-gutter">
                                                                    {i + 1}
                                                                </span>
                                                                <span className="diff-content">
                                                                    {line}
                                                                </span>
                                                            </span>
                                                        );
                                                    })}
                                            </code>
                                        </pre>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    style={{
                                        background: 'var(--bg-soft)',
                                        border: '1px solid var(--border)',
                                        borderRadius: '8px',
                                        padding: '24px',
                                        textAlign: 'center',
                                        color: 'var(--text-muted)',
                                        fontSize: '13px'
                                    }}
                                >
                                    {codeArtifact?.url ? (
                                        <a
                                            href={codeArtifact.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                color: 'var(--accent-light)'
                                            }}
                                        >
                                            View diff in pull request ↗
                                        </a>
                                    ) : (
                                        <span>
                                            Diff will be available once the fix
                                            is generated.
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Right Sticky Sidebar */}
                <div className="side-panel">
                    {/* Ask about this incident — primary action, kept in the first fold */}
                    <IncidentChat investigationId={data.id} />

                    {/* Remediation Panel — only shown when a plan or artifact exists, and not for DB/Postgres alerts */}
                    {!isDbAlert &&
                        ((data.remediation?.plan?.length ?? 0) > 0 ||
                            (data.remediation?.artifacts?.length ?? 0) > 0) && (
                            <RemediationPanel
                                remediation={data.remediation ?? null}
                                investigationId={data.id}
                                onRetry={() => void handleRetryRemediation()}
                                retrying={retrying}
                                retryError={retryError}
                                onConnectGitHub={() =>
                                    void handleConnectGitHub()
                                }
                                onReconnectGitHub={() =>
                                    void handleReconnectGitHub()
                                }
                                reconnecting={reconnecting}
                            />
                        )}

                    {/* Continuous Investigation Timeline */}
                    <div
                        className="timeline-section"
                        style={{
                            background: 'var(--bg-card)',
                            border: '1px solid var(--border)',
                            borderRadius: '8px',
                            padding: '16px'
                        }}
                    >
                        <h3
                            style={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: 'var(--text-h)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                margin: '0 0 16px'
                            }}
                        >
                            Chain of Custody Timeline
                        </h3>

                        <ul className="timeline-list">
                            <li className="timeline-item">
                                <span
                                    className="timeline-dot"
                                    style={{ background: 'var(--error)' }}
                                />
                                <div
                                    className="timeline-body"
                                    style={{
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        gap: '2px'
                                    }}
                                >
                                    <span
                                        className="timeline-ts"
                                        style={{ minWidth: 'auto' }}
                                    >
                                        {new Date(
                                            data.created_at
                                        ).toLocaleTimeString()}
                                    </span>
                                    <span
                                        className="timeline-event"
                                        style={{
                                            color: 'var(--text-h)',
                                            fontWeight: 500
                                        }}
                                    >
                                        Alert triggered: {data.alert_name}
                                    </span>
                                </div>
                            </li>

                            {journeySteps.map((ev, i) => (
                                <li className="timeline-item" key={i}>
                                    <span
                                        className="timeline-dot"
                                        style={{ background: 'var(--info)' }}
                                    />
                                    <div
                                        className="timeline-body"
                                        style={{
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            gap: '2px'
                                        }}
                                    >
                                        <span
                                            className="timeline-ts"
                                            style={{ minWidth: 'auto' }}
                                        >
                                            {new Date(
                                                ev.timestamp
                                            ).toLocaleTimeString()}
                                        </span>
                                        <span className="timeline-event">
                                            Queried {ev.tool}
                                        </span>
                                    </div>
                                </li>
                            ))}

                            {rca?.root_cause && (
                                <li className="timeline-item">
                                    <span
                                        className="timeline-dot"
                                        style={{ background: 'var(--success)' }}
                                    />
                                    <div
                                        className="timeline-body"
                                        style={{
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            gap: '2px'
                                        }}
                                    >
                                        <span
                                            className="timeline-ts"
                                            style={{ minWidth: 'auto' }}
                                        >
                                            {new Date(
                                                data.updated_at
                                            ).toLocaleTimeString()}
                                        </span>
                                        <span
                                            className="timeline-event"
                                            style={{ color: 'var(--success)' }}
                                        >
                                            Isolated root cause
                                        </span>
                                    </div>
                                </li>
                            )}

                            {hasRemediation && (
                                <li
                                    className="timeline-item"
                                    style={{ paddingBottom: 0 }}
                                >
                                    <span
                                        className="timeline-dot"
                                        style={{ background: 'var(--accent)' }}
                                    />
                                    <div
                                        className="timeline-body"
                                        style={{
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            gap: '2px'
                                        }}
                                    >
                                        <span
                                            className="timeline-ts"
                                            style={{ minWidth: 'auto' }}
                                        >
                                            {new Date(
                                                data.updated_at
                                            ).toLocaleTimeString()}
                                        </span>
                                        <span
                                            className="timeline-event"
                                            style={{
                                                color: 'var(--accent-light)'
                                            }}
                                        >
                                            {hasCodeFix
                                                ? 'Proposed code fix'
                                                : 'Proposed remediation'}
                                        </span>
                                    </div>
                                </li>
                            )}
                        </ul>
                    </div>

                    {/* Security Query Audit Logs */}
                    <div className="audit-logs-box" style={{ padding: '16px' }}>
                        <h3
                            className="audit-logs-title"
                            style={{ fontSize: '13px', margin: '0 0 12px' }}
                        >
                            Security Audit Slice
                        </h3>
                        <table
                            className="audit-table"
                            style={{ fontSize: '11px' }}
                        >
                            <thead>
                                <tr>
                                    <th>API Connection</th>
                                    <th>Telemetry window</th>
                                </tr>
                            </thead>
                            <tbody>
                                {buildAuditRows(rca?.evidence ?? []).map(
                                    (row) => (
                                        <tr key={row.api}>
                                            <td>{row.api}</td>
                                            <td>
                                                <span
                                                    className="audit-scope-tag"
                                                    style={{ fontSize: '9px' }}
                                                >
                                                    {row.window}
                                                </span>
                                            </td>
                                        </tr>
                                    )
                                )}
                                {(rca?.evidence ?? []).length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={2}
                                            style={{
                                                color: 'var(--text-muted)',
                                                textAlign: 'center',
                                                padding: '8px 0'
                                            }}
                                        >
                                            No tool calls recorded
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
